import {createEntitydReducer} from './createEntitydReducer';
import {EntityState} from '../types';
import {
  TAdd,
  TDelete,
  TUpdate,
  TReset,
  TAddType,
  TDeleteType,
  TUpdateType,
  TResetType,
  TReplace,
  TReplaceType,
} from '../types/actions';
import {MiddlewareAPI} from 'redux';
import {Dispatch} from 'react';
import {query} from '@di-nxt/query';

export const createEntityReduxFactory = <
  TData extends Record<TIdKey, any>,
  TEntityName extends string = any,
  TIdKey extends keyof TData = keyof TData
>(
  entityName: TEntityName,
  idKey: TIdKey,
  hook?: (storeApi: MiddlewareAPI, action: any) => void,
) => {
  // Create reducer for resource
  const reducer = createEntitydReducer<TData, TEntityName, TIdKey>(
    entityName,
    idKey,
  );

  // Create add action factory
  const add = (data: TData[]): TAdd<TData, TEntityName> => ({
    type: `ADD_${entityName}` as TAddType<TEntityName>,
    payload: {
      data,
    },
  });

  // Create remove action factory
  const remove = (
    id: TData[TIdKey][],
  ): TDelete<TData, TIdKey, TEntityName> => ({
    type: `DELETE_${entityName}` as TDeleteType<TEntityName>,
    payload: {id},
  });

  // Create update action factory
  const update = (
    id: TData[TIdKey],
    data: Partial<Omit<TData, TIdKey>>,
  ): TUpdate<TData, TIdKey, TEntityName> => ({
    type: `UPDATE_${entityName}` as TUpdateType<TEntityName>,
    payload: {
      data: {[idKey]: id, ...data} as TData,
      id,
    },
  });

  // Create replace action factory
  const replace = (data: TData[]): TReplace<TData, TIdKey, TEntityName> => ({
    type: `REPLACE_${entityName}` as TReplaceType<TEntityName>,
    payload: {
      data,
    },
  });

  // Create reset action factory
  const reset = (): TReset<TEntityName> => ({
    type: `RESET_${entityName}` as TResetType<TEntityName>,
    payload: {},
  });

  // Create all selector
  const all = (state: {[key in TEntityName]: EntityState<TData, TIdKey>}) =>
    state[entityName].data;

  // Create single selector (by id)
  const single = (id: TData[TIdKey]) => (
    state: {[key in TEntityName]: EntityState<TData, TIdKey>},
  ): TData | undefined =>
    query(state[entityName].data || [])
      .where(item => item[idKey] === id)
      .first();

  // Create entity state version selector
  const version = (state: {[key in TEntityName]: EntityState<TData, TIdKey>}) =>
    state[entityName].version;

  // Create middleware (if any). TODO: shoud be undefined if no hook
  const middleware = (api: MiddlewareAPI) => (next: Dispatch<any>) => (action: {
    type: string;
    payload: unknown;
  }) => {
    hook && hook(api, action);
    return next(action);
  };

  return {
    key: entityName,
    reducer,
    middleware,
    actions: {
      add,
      remove,
      update,
      replace,
      reset,
    },
    selectors: {
      all,
      single,
      version,
    },
  };
};
